import { Map, Record } from 'immutable';

import { SubscriptionItemEntity } from '../components/subscription-list/subscription-item.entity';
import { ProjectItemEntity } from '../components/subscription-view/consumer-config-modal/project-item.entity';
import { ViewDetailsEntity } from '../components/subscription-view/view-details.entity';
import { SwitchableVersionProjectItemEntity } from '../components/version-switcher-modal/switchable-version-project-item.entity';
import {
	SubscriptionAction,
	SubscriptionStatus,
} from '../domain/subscription.types';

export const SubscriptionSliceKey = 'main';

export interface ISubscriptionOriginalSliceState {
	loading: boolean;
	subscriptions: Map<string, SubscriptionItemEntity>;
	totalItems: number;
	hasCreatePermission: boolean;
	inViewSubscription: ViewDetailsEntity;
	projects: Map<string, ProjectItemEntity>;
	switchableConsumedProjects: Map<string, SwitchableVersionProjectItemEntity>;
	isSwitchingVersion: boolean;
}

export const defaults: ISubscriptionOriginalSliceState = {
	loading: false,
	subscriptions: Map(),
	totalItems: 0,
	hasCreatePermission: false,
	inViewSubscription: ViewDetailsEntity.build(),
	projects: Map(),
	switchableConsumedProjects: Map(),
	isSwitchingVersion: false,
};

export class SubscriptionOriginalSliceState extends Record<ISubscriptionOriginalSliceState>(
	defaults
) {
	public static readonly key = SubscriptionSliceKey;

	public setLoading(isLoading: boolean): SubscriptionOriginalSliceState {
		return this.set('loading', isLoading);
	}

	public setSubscriptions(
		elements: Map<string, SubscriptionItemEntity>
	): SubscriptionOriginalSliceState {
		return this.set('subscriptions', elements);
	}

	public setTotalItems(total: number): SubscriptionOriginalSliceState {
		return this.set('totalItems', total);
	}

	public setHasCreatePermission(
		canCreate: boolean
	): SubscriptionOriginalSliceState {
		return this.set('hasCreatePermission', canCreate);
	}

	public setInViewSubscription(
		subscription: ViewDetailsEntity
	): SubscriptionOriginalSliceState {
		return this.set('inViewSubscription', subscription);
	}

	public setProjects(
		elements: Map<string, ProjectItemEntity>
	): SubscriptionOriginalSliceState {
		return this.set('projects', elements);
	}

	public setSwitchableConsumedProjects(
		elements: Map<string, SwitchableVersionProjectItemEntity>
	): SubscriptionOriginalSliceState {
		return this.set('switchableConsumedProjects', elements);
	}

	public setIsSwitchingVersion(
		isSwitching: boolean
	): SubscriptionOriginalSliceState {
		return this.set('isSwitchingVersion', isSwitching);
	}

	public setSubscriptionActionInProgress(
		id: number,
		action: SubscriptionAction
	): SubscriptionOriginalSliceState {
		const elem = this.subscriptions.get(`${id}`);
		if (!elem) {
			return this;
		}

		let updatedElem = elem.setActionInProgress(action);

		if (action !== 'none') {
			updatedElem = updatedElem.setStatus(SubscriptionStatus.Waiting);
		}

		const updated = this.subscriptions.set(`${id}`, updatedElem);

		return this.set('subscriptions', updated);
	}

	public resetSubscription(): SubscriptionOriginalSliceState {
		return this.set('inViewSubscription', defaults.inViewSubscription);
	}
}
